/**
 * Internal dependencies
 */
import { initCookieSettingsLinks } from './utils/cookie-settings';

const MAINTENANCE_BANNER_DISMISSED_KEY = 'spotlightMaintenanceBannerDismissed';

function isMaintenanceBannerDismissed() {
  try {
    return window.sessionStorage.getItem( MAINTENANCE_BANNER_DISMISSED_KEY ) === 'true';
  } catch ( error ) {
    return false;
  }
}

function setMaintenanceBannerDismissed() {
  window.sessionStorage.setItem( MAINTENANCE_BANNER_DISMISSED_KEY, 'true' );
}

function getMaintenanceBannerCloseButton( banner ) {
  return Array.from(
    banner.querySelectorAll( '.maintenance-banner__inner > .wp-block-buttons .wp-block-button__link' ),
  ).find( function ( button ) {
    return button.textContent.trim().toLowerCase() === 'x';
  } );
}

function initMaintenanceBanner() {
  const banners = document.querySelectorAll( '.maintenance-banner' );

  if ( ! banners.length ) {
    return;
  }

  banners.forEach( function ( banner ) {
    const closeButton = getMaintenanceBannerCloseButton( banner );

    if ( isMaintenanceBannerDismissed() ) {
      banner.hidden = true;
      return;
    }

    if ( ! closeButton ) {
      return;
    }

    closeButton.setAttribute( 'role', 'button' );
    closeButton.setAttribute( 'tabindex', '0' );
    closeButton.setAttribute( 'aria-label', 'Close maintenance banner' );

    const closeBanner = function ( event ) {
      event.preventDefault();
      banner.hidden = true;
      setMaintenanceBannerDismissed();
    };

    closeButton.addEventListener( 'click', closeBanner );

    closeButton.addEventListener( 'keydown', function ( event ) {
      if ( event.key === 'Enter' || event.key === ' ' ) {
        closeBanner( event );
      }
    } );
  } );
}

// Must match the inline pre-paint check in template-parts/global/announcement-banner.php.
const ANNOUNCEMENT_BANNER_DISMISSED_KEY = 'spotlightAnnouncementDismissed';

// Scroll distance (px) after which the banner slides away when Hide on Scroll is on.
const ANNOUNCEMENT_BANNER_COLLAPSE_OFFSET = 80;

function initAnnouncementBanner() {
  const banner = document.getElementById( 'announcement-banner' );

  if ( ! banner || banner.hidden ) {
    return;
  }

  const root = document.documentElement;
  const setBannerHeight = function () {
    // Fractional height; offsetHeight rounds and leaves a sub-pixel gap above the header.
    root.style.setProperty( '--announcement-banner-height', banner.getBoundingClientRect().height + 'px' );
  };

  // Keep the header offset correct when the message wraps (resize, font load).
  let resizeObserver = null;

  if ( 'ResizeObserver' in window ) {
    resizeObserver = new ResizeObserver( setBannerHeight );
    resizeObserver.observe( banner );
  }

  setBannerHeight();

  // Slide the banner away once the page is scrolled; bring it back near the top.
  // Enabled per banner from Sitewide Settings > Announcement Banner > Hide on Scroll.
  const hideOnScroll = banner.hasAttribute( 'data-hide-on-scroll' );
  let scrollTicking = false;
  const updateCollapsed = function () {
    root.classList.toggle(
      'is-announcement-banner-collapsed',
      window.scrollY > ANNOUNCEMENT_BANNER_COLLAPSE_OFFSET,
    );
    scrollTicking = false;
  };
  const onScroll = function () {
    if ( ! scrollTicking ) {
      scrollTicking = true;
      window.requestAnimationFrame( updateCollapsed );
    }
  };

  if ( hideOnScroll ) {
    window.addEventListener( 'scroll', onScroll, { passive: true } );
    updateCollapsed();
  }

  const closeButton = banner.querySelector( '.announcement-banner__close' );

  if ( ! closeButton ) {
    return;
  }

  closeButton.addEventListener( 'click', function () {
    banner.hidden = true;
    root.classList.remove( 'has-announcement-banner', 'is-announcement-banner-collapsed' );
    root.style.removeProperty( '--announcement-banner-height' );
    window.removeEventListener( 'scroll', onScroll );

    if ( resizeObserver ) {
      resizeObserver.disconnect();
    }

    try {
      window.localStorage.setItem( ANNOUNCEMENT_BANNER_DISMISSED_KEY, banner.dataset.bannerId );
    } catch ( error ) {
      // Storage unavailable (e.g. private mode); banner stays closed for this page view only.
    }
  } );
}

document.addEventListener( "DOMContentLoaded", function () {
  // Initialize cookie settings links
  initCookieSettingsLinks();
  initMaintenanceBanner();
  initAnnouncementBanner();
} );
